<template>
  <v-container class="py-6" fluid>
    <div class="d-flex align-center mb-6">
      <v-btn icon color="primary" class="mr-3 bg-primary-light" elevation="2">
        <v-icon>mdi-google-classroom</v-icon>
      </v-btn>
      <div>
        <h1 class="text-h5 font-weight-bold primary--text mb-0">
          Asignar Salones a Maestros (Drag & Drop)
        </h1>
        <span class="text-caption grey--text font-weight-medium">
          Asigna salones de clase arrastrándolos al maestro deseado
        </span>
      </div>
    </div>

    <v-row>
      <!-- LEFT SECTION: CLASSROOMS -->
      <v-col cols="12" md="6">
        <v-card class="elevation-12 rounded-xl overflow-hidden glass-card fill-height">
          <v-toolbar flat color="transparent" class="px-4 pt-4">
            <v-icon left color="primary">mdi-google-classroom</v-icon>
            <v-toolbar-title class="font-weight-bold primary--text">Salones de Clase</v-toolbar-title>
          </v-toolbar>

          <v-card-text class="pa-6">
            <v-text-field
              v-model="searchClassroom"
              append-icon="mdi-magnify"
              :label="$t('Buscar')"
              single-line
              hide-details
              outlined
              dense
              class="rounded-lg bg-grey-lighten-4 mb-6"
            ></v-text-field>

            <v-divider class="mb-4"></v-divider>

            <div class="list-container custom-scrollbar">
              <v-progress-linear v-if="loadingClassrooms" indeterminate color="primary"></v-progress-linear>
              
              <v-row v-if="filteredClassrooms.length > 0">
                <v-col 
                  v-for="classroom in filteredClassrooms" 
                  :key="classroom.id" 
                  cols="12"
                  class="py-1"
                >
                  <div 
                    class="classroom-card d-flex align-center pa-3 rounded-lg border shadow-xs"
                    draggable="true"
                    @dragstart="onDragStart($event, classroom)"
                  >
                    <v-avatar size="40" color="primary lighten-4" class="mr-3 elevation-1">
                      <v-icon color="primary">mdi-chair-school</v-icon>
                    </v-avatar>
                    <div class="flex-grow-1">
                      <div class="font-weight-bold text-subtitle-2">{{ classroom.name }}</div>
                      <div class="text-caption grey--text">{{ classroom.grade_name }}</div>
                    </div>
                    <v-icon color="grey lighten-1">mdi-drag-variant</v-icon>
                  </div>
                </v-col>
              </v-row>
              
              <div v-else-if="!loadingClassrooms" class="text-center py-12">
                <v-icon size="64" color="grey lighten-3">mdi-google-classroom</v-icon>
                <p class="grey--text mt-4">No se encontraron salones de clase</p>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- RIGHT SECTION: TEACHER ASSIGNMENT -->
      <v-col cols="12" md="6">
        <v-card 
          class="elevation-12 rounded-xl overflow-hidden glass-card fill-height"
          :class="{'drop-zone-active': isDraggingOver}"
          @dragover.prevent="isDraggingOver = true"
          @dragleave="isDraggingOver = false"
          @drop="onDrop"
        >
          <v-toolbar flat color="transparent" class="px-4 pt-4">
            <v-icon left color="secondary">mdi-account-tie</v-icon>
            <v-toolbar-title class="font-weight-bold secondary--text">Maestro</v-toolbar-title>
          </v-toolbar>

          <v-card-text class="pa-6">
            <v-autocomplete
              v-model="selectedTeacherId"
              :items="teachers"
              item-text="idUser.first_name"
              item-value="id"
              label="Elige un maestro"
              outlined
              dense
              clearable
              prepend-inner-icon="mdi-account-search"
              class="rounded-lg bg-grey-lighten-4 mb-6"
              @change="onTeacherSelected"
              :filter="customTeacherFilter"
            >
              <template v-slot:selection="data">
                {{ data.item.idUser.first_name }} {{ data.item.idUser.last_name }}
              </template>
              <template v-slot:item="data">
                <v-list-item-avatar>
                  <img :src="data.item.idUser.avatar || defaultAvatar" />
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title>{{ data.item.idUser.first_name }} {{ data.item.idUser.last_name }}</v-list-item-title>
                  <v-list-item-subtitle>{{ data.item.position }}</v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>

            <v-divider class="mb-4"></v-divider>

            <div 
              class="drop-area custom-scrollbar"
              :class="{'has-teacher': !!selectedTeacherId}"
            >
              <div v-if="!selectedTeacherId" class="text-center py-12">
                <v-icon size="64" color="grey lighten-3">mdi-account-outline</v-icon>
                <p class="grey--text mt-4">Selecciona un maestro para asignar salones de clase</p>
              </div>

              <div v-else>
                <div class="d-flex align-center mb-4">
                  <v-chip 
                    color="secondary" 
                    dark 
                    class="font-weight-bold"
                  >
                    <v-avatar left>
                      <v-img :src="getSelectedTeacherAvatar() || defaultAvatar"></v-img>
                    </v-avatar>
                    {{ getSelectedTeacherName() }}
                  </v-chip>
                  <v-spacer></v-spacer>
                  <span class="text-caption font-weight-bold grey--text">
                    {{ assignmentList.length }} SALONES ASIGNADOS
                  </span>
                </div>

                <v-progress-linear v-if="loadingAssignments" indeterminate color="secondary" class="mb-4"></v-progress-linear>

                <div v-if="assignmentList.length > 0">
                  <v-row>
                    <v-col 
                      v-for="assignment in assignmentList" 
                      :key="assignment.id" 
                      cols="12"
                      class="py-1"
                    >
                      <v-hover v-slot="{ hover }">
                        <div class="enrolled-card d-flex align-center pa-3 rounded-lg border shadow-xs transition-all" :class="{'elevation-4': hover}">
                          <v-avatar size="40" color="secondary lighten-4" class="mr-3 elevation-1">
                            <v-icon color="secondary">mdi-chair-school</v-icon>
                          </v-avatar>
                          <div class="flex-grow-1">
                            <div class="font-weight-bold text-subtitle-2">{{ assignment.name }}</div>
                            <div class="text-caption grey--text">{{ assignment.grade_name }}</div>
                          </div>
                          <div class="d-flex">
                            <v-btn 
                              icon 
                              small 
                              color="error" 
                              class="opacity-0 transition-all shadow-sm bg-white" 
                              :class="{'opacity-100': hover}"
                              @click="confirmDelete(assignment)"
                            >
                              <v-icon small>mdi-trash-can-outline</v-icon>
                            </v-btn>
                          </div>
                        </div>
                      </v-hover>
                    </v-col>
                  </v-row>
                </div>
                
                <div v-else-if="!loadingAssignments" class="text-center py-12 drop-instruction border-dashed rounded-xl border-2">
                  <v-icon size="48" color="primary" class="animate-bounce">mdi-tray-arrow-down</v-icon>
                  <p class="primary--text font-weight-bold mt-4">Arrastra salones aquí para asignarlos</p>
                  <span class="text-caption grey--text">Solo suelta un salon para completar el proceso</span>
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Confirm Delete Dialog -->
    <v-dialog v-model="dialogDelete" max-width="450px" transition="dialog-center-transition">
      <v-card class="rounded-xl overflow-hidden glass-card p-4">
        <v-card-title class="headline d-flex align-center pb-2">
          <v-icon color="error" large class="mr-3">mdi-alert-circle-outline</v-icon>
          <span class="font-weight-bold">{{ $t('aviso') }}</span>
        </v-card-title>

        <v-card-text class="pt-4 text-body-1 dark--text font-weight-medium">
          ¿Estás seguro de que deseas eliminar este salón asignado al maestro?
          <div v-if="itemToDelete" class="mt-4 pa-4 bg-grey-lighten-4 rounded-lg border-l-4-error shadow-inner">
            <div class="d-flex align-center mb-2">
              <v-icon x-small color="grey" class="mr-2">mdi-google-classroom</v-icon>
              <span class="text-caption font-weight-bold grey--text text-uppercase tracking-wider">Salón</span>
            </div>
            <div class="font-weight-bold primary--text mb-3">
              {{ itemToDelete.name }}
            </div>
          </div>
        </v-card-text>

        <v-card-actions class="pa-6">
          <v-spacer></v-spacer>
          <v-btn text class="rounded-pill px-6" @click="dialogDelete = false">No</v-btn>
          <v-btn
            color="error"
            class="rounded-pill px-8 elevation-4 shadow-error hover-scale transition-all"
            @click="deleteItemConfirm"
            :loading="deleting"
          >
            {{ $t('Si') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { HTTP } from '@/main.js'
import store from '@/store'

export default {
  name: 'TeacherClassroomDragDrop',
  data: () => ({
    searchClassroom: '',
    loadingData: false,
    loadingClassrooms: false,
    loadingTeachers: false,
    loadingAssignments: false,
    deleting: false,
    
    selectedTeacherId: null,
    isDraggingOver: false,
    dialogDelete: false,
    itemToDelete: null,

    allClassrooms: [],
    teachers: [],
    assignmentList: [],
    
    defaultAvatar: 'https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png'
  }),

  computed: {
    filteredClassrooms() {
      let filtered = this.allClassrooms;
      
      if (this.searchClassroom) {
        const query = this.searchClassroom.toLowerCase();
        filtered = filtered.filter(c => 
          c.name.toLowerCase().includes(query) ||
          c.grade_name.toLowerCase().includes(query)
        );
      }
      
      // Filter out classrooms already assigned to selected teacher
      if (this.selectedTeacherId) {
        filtered = filtered.filter(c => 
          !this.assignmentList.some(a => a.idClassroom === c.id)
        );
      }
      
      return filtered;
    }
  },

  created() {
    this.init();
  },

  methods: {
    customTeacherFilter(item, queryText) {
      if (!queryText) return true;
      const normalize = (str) => {
        return str ? str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase() : '';
      };
      
      const firstName = item.idUser.first_name || '';
      const lastName = item.idUser.last_name || '';
      const fullName = `${firstName} ${lastName}`;
      
      return normalize(fullName).includes(normalize(queryText));
    },

    async init() {
      this.loadingData = true;
      try {
        await Promise.all([
          this.getClassrooms(),
          this.getTeachers()
        ]);
      } catch (error) {
        console.error('Error loading initial data:', error);
      } finally {
        this.loadingData = false;
      }
    },

    getClassrooms() {
      this.loadingClassrooms = true;
      return HTTP.get('classrooms/', {
        headers: { 'authorization': 'Token ' + store.getters.getToken }
      }).then(res => {
        this.allClassrooms = res.data;
      }).finally(() => { this.loadingClassrooms = false; });
    },

    getTeachers() {
      this.loadingTeachers = true;
      return HTTP.get('teacher/', {
        headers: { 'authorization': 'Token ' + store.getters.getToken }
      }).then(res => {
        this.teachers = res.data;
      }).finally(() => { this.loadingTeachers = false; });
    },

    onTeacherSelected() {
      this.assignmentList = [];
      if (!this.selectedTeacherId) {
        return;
      }
      this.getAssignedClassrooms();
    },

    getAssignedClassrooms() {
      if (!this.selectedTeacherId) return;
      this.loadingAssignments = true;
      HTTP.get(`teacher/${this.selectedTeacherId}/classroom/`, {
        headers: { 'authorization': 'Token ' + store.getters.getToken }
      }).then(res => {
        this.assignmentList = res.data;
      }).catch(err => {
        console.error('Error fetching assigned classrooms:', err);
      }).finally(() => {
        this.loadingAssignments = false;
      });
    },

    getSelectedTeacherName() {
      const teacher = this.teachers.find(t => t.id === this.selectedTeacherId);
      return teacher && teacher.idUser ? `${teacher.idUser.first_name} ${teacher.idUser.last_name}` : '';
    },

    getSelectedTeacherAvatar() {
      const teacher = this.teachers.find(t => t.id === this.selectedTeacherId);
      return teacher && teacher.idUser ? teacher.idUser.avatar : null;
    },

    // DRAG AND DROP HANDLERS
    onDragStart(event, classroom) {
      event.dataTransfer.setData('classroomId', classroom.id);
      event.dataTransfer.effectAllowed = 'copy';
    },

    onDrop(event) {
      this.isDraggingOver = false;
      const classroomId = event.dataTransfer.getData('classroomId');
      
      if (!classroomId) return;
      if (!this.selectedTeacherId) {
        this.showSnackbar('Por favor selecciona un maestro', 'warning');
        return;
      }

      this.assignClassroom(classroomId);
    },

    assignClassroom(idClassroom) {
      if (this.assignmentList.some(a => a.idClassroom === idClassroom)) {
        this.showSnackbar('El salón ya está asignado', 'warning');
        return;
      }

      this.loadingAssignments = true;
      const url = `teacher/${this.selectedTeacherId}/classroom/`;
      
      HTTP.post(url, {
        idClassroom
      }, {
        headers: { 'authorization': 'Token ' + store.getters.getToken }
      }).then(() => {
        this.showSnackbar('Salón asignado correctamente', 'success');
        this.getAssignedClassrooms();
      }).catch(err => {
        console.error('Error assigning classroom:', err);
        this.showSnackbar('Error al asignar el salón', 'error');
        this.loadingAssignments = false;
      });
    },

    confirmDelete(assignment) {
      this.itemToDelete = assignment;
      this.dialogDelete = true;
    },

    deleteItemConfirm() {
      this.deleting = true;
      const url = `teacher/${this.selectedTeacherId}/classroom/${this.itemToDelete.idClassroom}`;
      
      HTTP.delete(url, {
        headers: { 'authorization': 'Token ' + store.getters.getToken }
      }).then(() => {
        this.showSnackbar('Salón desasignado correctamente', 'success');
        this.getAssignedClassrooms();
        this.dialogDelete = false;
      }).catch(err => {
        console.error('Error deleting assignment:', err);
        this.showSnackbar('Error al desasignar el salón', 'error');
      }).finally(() => { 
        this.deleting = false; 
      });
    },

    clear() {
      this.$store.commit('snackbar_activate_false')
    },

    showSnackbar(message, color) {
      this.$store.commit('snackbar_change_message', message)
      this.$store.commit('snackbar_change_color', color)
      this.$store.commit('snackbar_activate_true')
      setTimeout(this.clear, 3000)
    }
  }
}
</script>

<style scoped>
.glass-card {
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.bg-primary-light {
  background-color: rgba(var(--v-primary-base), 0.08) !important;
}

.bg-grey-lighten-4 {
  background-color: #f8f9fa !important;
}

.shadow-xs {
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05) !important;
}

.list-container, .drop-area {
  height: 500px;
  overflow-y: auto;
  padding: 4px;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #e0e0e0;
  border-radius: 10px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #bdbdbd;
}

.classroom-card {
  border: 1px solid #edf2f7;
  background-color: white;
  transition: all 0.2s ease;
  cursor: grab;
}
.classroom-card:hover {
  border-color: var(--v-primary-base);
  background-color: rgba(var(--v-primary-base), 0.02);
  transform: translateX(4px);
}
.classroom-card:active {
  cursor: grabbing;
}

.enrolled-card {
  border: 1px solid #edf2f7;
  background-color: white;
}

.drop-zone-active {
  background-color: rgba(var(--v-primary-base), 0.05) !important;
  border: 2px dashed var(--v-primary-base) !important;
}

.drop-instruction {
  border-color: #e2e8f0;
  background-color: #f8fafc;
}

.border-dashed {
  border-style: dashed !important;
}

.border-2 {
  border-width: 2px !important;
}

.animate-bounce {
  animation: bounce 2s infinite;
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% {transform: translateY(0);}
  40% {transform: translateY(-20px);}
  60% {transform: translateY(-10px);}
}

.opacity-0 { opacity: 0; }
.opacity-100 { opacity: 1; }
.hover-scale:hover {
  transform: translateY(-2px);
}
.shadow-error {
  box-shadow: 0 4px 14px 0 rgba(255, 82, 82, 0.3) !important;
}
.border-l-4-error {
  border-left: 4px solid #ff5252;
}
.tracking-wider {
  letter-spacing: 0.5px;
}
.shadow-inner {
  box-shadow: inset 0 2px 4px 0 rgba(0, 0, 0, 0.05);
}
.shadow-sm {
  box-shadow: 0 2px 4px rgba(0,0,0,0.1) !important;
}
</style>
